import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { PageHeader, Card, Table, Badge, Input, Select, Spinner, QueryError } from "../components";
import { usePolling } from "../hooks/usePolling";
import { apiGet } from "../lib/api";

interface AuditEntry {
  id: string;
  timestamp: string;
  actor: string;
  action: string;
  target: string;
  details: string;
}

const ACTION_BADGE_COLOR: Record<string, "red" | "amber" | "blue" | "green" | "gray"> = {
  security_alert: "red",
  status_change: "amber",
  heartbeat_missed: "amber",
  config_update: "blue",
  version_deploy: "blue",
  tenant_created: "green",
  tenant_suspended: "red",
  deployment_scaled: "blue",
  cost_alert: "amber",
  learning_sync: "green",
  backup_completed: "green",
  certificate_renewed: "green",
  experiment_promoted: "green",
  heartbeat_received: "gray",
};

const TIME_RANGE_OPTIONS = [
  { value: "all", label: "All time" },
  { value: "1", label: "Last 1 hour" },
  { value: "4", label: "Last 4 hours" },
  { value: "12", label: "Last 12 hours" },
  { value: "24", label: "Last 24 hours" },
  { value: "48", label: "Last 48 hours" },
];

function getActionTypes(entries: AuditEntry[]): { value: string; label: string }[] {
  const types = new Set(entries.map((e) => e.action));
  const options: { value: string; label: string }[] = [{ value: "all", label: "All actions" }];
  for (const t of Array.from(types).sort()) {
    options.push({ value: t, label: t.replace(/_/g, " ") });
  }
  return options;
}

function formatTimestamp(ts: string): string {
  const d = new Date(ts);
  return d.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

export default function AuditLog() {
  const [search, setSearch] = useState("");
  const [actionFilter, setActionFilter] = useState("all");
  const [timeRange, setTimeRange] = useState("all");

  const {
    data: auditEntries,
    isLoading,
    error,
  } = useQuery<AuditEntry[]>({
    queryKey: ["fleet", "audit"],
    queryFn: () => apiGet<AuditEntry[]>("/api/fleet/audit"),
  });

  usePolling([["fleet", "audit"]], 30_000);

  const actionOptions = useMemo(() => getActionTypes(auditEntries ?? []), [auditEntries]);

  const filtered = useMemo(() => {
    if (!auditEntries) return [];
    let result = auditEntries;

    // Filter by time range
    if (timeRange !== "all") {
      const hours = parseInt(timeRange, 10);
      const cutoff = Date.now() - hours * 60 * 60 * 1000;
      result = result.filter((e) => new Date(e.timestamp).getTime() >= cutoff);
    }

    // Filter by action type
    if (actionFilter !== "all") {
      result = result.filter((e) => e.action === actionFilter);
    }

    // Filter by search text
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter(
        (e) =>
          e.actor.toLowerCase().includes(q) ||
          e.action.toLowerCase().includes(q) ||
          e.target.toLowerCase().includes(q) ||
          e.details.toLowerCase().includes(q),
      );
    }

    return result;
  }, [auditEntries, search, actionFilter, timeRange]);

  if (isLoading && !auditEntries) {
    return (
      <div>
        <PageHeader title="Audit Log" subtitle="Platform-wide audit trail" />
        <div className="flex items-center justify-center py-20">
          <Spinner size={24} />
        </div>
      </div>
    );
  }

  if (error && !auditEntries) {
    return (
      <div>
        <PageHeader title="Audit Log" subtitle="Platform-wide audit trail" />
        <QueryError error={error} queryKeys={[["fleet", "audit"]]} />
      </div>
    );
  }

  const columns = [
    {
      key: "timestamp",
      header: "Timestamp",
      render: (row: AuditEntry) => (
        <span className="font-mono text-xs" style={{ color: "var(--c-text-secondary)" }}>
          {formatTimestamp(row.timestamp)}
        </span>
      ),
    },
    {
      key: "actor",
      header: "Actor",
      render: (row: AuditEntry) => (
        <span
          className="text-sm"
          style={{
            color: row.actor === "system" ? "var(--c-text-faint)" : "var(--c-text)",
            fontStyle: row.actor === "system" ? "italic" : "normal",
          }}
        >
          {row.actor}
        </span>
      ),
    },
    {
      key: "action",
      header: "Action",
      render: (row: AuditEntry) => (
        <Badge color={ACTION_BADGE_COLOR[row.action] ?? "gray"}>
          {row.action.replace(/_/g, " ")}
        </Badge>
      ),
    },
    {
      key: "target",
      header: "Target",
      render: (row: AuditEntry) => (
        <span className="font-mono text-xs" style={{ color: "var(--c-text-secondary)" }}>
          {row.target}
        </span>
      ),
    },
    {
      key: "details",
      header: "Details",
      render: (row: AuditEntry) => (
        <span className="text-xs" style={{ color: "var(--c-text-secondary)" }}>
          {row.details}
        </span>
      ),
    },
  ];

  return (
    <div>
      <PageHeader title="Audit Log" subtitle="Platform-wide audit trail" />

      {/* Filter row */}
      <div className="flex flex-wrap items-end gap-4 mb-6">
        <div className="flex-1 min-w-[200px]">
          <Input
            label="Search"
            placeholder="Search by actor, action, target, details..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="w-48">
          <Select
            label="Action Type"
            options={actionOptions}
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
          />
        </div>
        <div className="w-44">
          <Select
            label="Time Range"
            options={TIME_RANGE_OPTIONS}
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
          />
        </div>
      </div>

      {/* Results count */}
      <p className="text-xs mb-3" style={{ color: "var(--c-text-faint)" }}>
        {filtered.length} event{filtered.length !== 1 ? "s" : ""}
        {search || actionFilter !== "all" || timeRange !== "all" ? " (filtered)" : ""}
      </p>

      {/* Audit table */}
      <Card padding={false}>
        <Table
          columns={columns}
          data={filtered as unknown as Record<string, unknown>[]}
          keyField="id"
          loading={isLoading}
          emptyMessage="No audit events match the current filters"
        />
      </Card>
    </div>
  );
}
